import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Eye, Edit2, CheckCircle, Trash2, Package, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LockerRepacks = ({ token, clients }) => {
  const [repacks, setRepacks] = useState([]);
  const [trackings, setTrackings] = useState([]);
  const [selectedRepacks, setSelectedRepacks] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    agencia: 'todas',
    tipoEnvio: 'cualquiera',
    desde: '',
    hasta: '',
    tipo: 'todos',
    estatus: 'todos'
  });
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedRepack, setSelectedRepack] = useState(null);
  
  // Edit modal - tracking editing
  const [editFormData, setEditFormData] = useState({
    shipping_method: 'Aereo',
    notes: '',
    tracking_ids: []
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Approval modal - full WR creation form (like "Crear WR desde Trackings")
  const [wrFormData, setWrFormData] = useState({
    selectedTrackingIds: [],
    wr_number: '',
    shipping_method: 'Aereo',
    package_type: 'Caja',
    pieces: 1,
    dim_largo: '',
    dim_ancho: '',
    dim_alto: '',
    pp_weight_lb: '',
    pvol: '',
    pmax: '',
    volume_ft3: '',
    notes: '',
    agent: 'Ninguno',
    description: ''
  });
  const [approving, setApproving] = useState(false);

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [repacksRes, trackingsRes] = await Promise.all([
        axios.get(`${API}/admin/repacks`, axiosConfig),
        axios.get(`${API}/admin/trackings`, axiosConfig)
      ]);
      setRepacks(repacksRes.data);
      setTrackings(trackingsRes.data);
    } catch (error) {
    }
  }, [axiosConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getClientInfo = (clientId) => {
    return clients.find(c => c.id === clientId) || {};
  };

  const getRepackTrackings = (repack) => {
    return trackings.filter(t => repack.tracking_ids.includes(t.id));
  };

  // Count available trackings for a client (received or in_repack)
  const getClientAvailableCount = (clientId) => {
    return trackings.filter(t => t.client_id === clientId && (t.status === 'received' || t.status === 'in_repack')).length;
  };

  // Get all trackings belonging to a specific client (for edit modal multi-select)
  const getClientTrackingsForEdit = (clientId, currentRepackTrackingIds) => {
    return trackings.filter(t => 
      t.client_id === clientId && 
      (t.status === 'received' || t.status === 'in_repack' || currentRepackTrackingIds.includes(t.id))
    );
  };

  const handleSelectAll = () => {
    if (selectedRepacks.length === filteredRepacks.length) {
      setSelectedRepacks([]);
    } else {
      setSelectedRepacks(filteredRepacks.map(r => r.id));
    }
  };

  const toggleSelectRepack = (repackId) => {
    if (selectedRepacks.includes(repackId)) {
      setSelectedRepacks(selectedRepacks.filter(id => id !== repackId));
    } else {
      setSelectedRepacks([...selectedRepacks, repackId]);
    }
  };

  // ===== VIEW MODAL =====
  const handleViewRepack = (repack) => {
    setSelectedRepack(repack);
    setViewModalOpen(true);
  };

  // ===== DELETE =====
  const handleDeleteRepack = async (repackId) => {
    if (!window.confirm('Estas seguro de eliminar esta solicitud de reempaque? Esta accion no se puede deshacer.')) return;
    
    try {
      await axios.delete(`${API}/admin/repacks/${repackId}`, axiosConfig);
      alert('Solicitud eliminada');
      fetchData();
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ===== EDIT MODAL (with tracking multi-select) =====
  const handleEditRepack = (repack) => {
    setSelectedRepack(repack);
    setEditFormData({
      shipping_method: repack.shipping_method || 'Aereo',
      notes: repack.notes || '',
      tracking_ids: [...(repack.tracking_ids || [])]
    });
    setEditModalOpen(true);
  };

  const toggleEditTrackingSelection = (trackingId) => {
    setEditFormData(prev => {
      if (prev.tracking_ids.includes(trackingId)) {
        return { ...prev, tracking_ids: prev.tracking_ids.filter(id => id !== trackingId) };
      } else {
        return { ...prev, tracking_ids: [...prev.tracking_ids, trackingId] };
      }
    });
  };

  const handleSaveEdit = async () => {
    if (editFormData.tracking_ids.length === 0) {
      alert('Debe seleccionar al menos un tracking');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/admin/repacks/${selectedRepack.id}`,
        {
          shipping_method: editFormData.shipping_method,
          notes: editFormData.notes,
          tracking_ids: editFormData.tracking_ids
        },
        axiosConfig
      );
      alert('Cambios guardados');
      setEditModalOpen(false);
      fetchData();
    } catch (error) {
      alert('Error al guardar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingEdit(false);
    }
  };

  // ===== APPROVE MODAL (Complete WR form) =====
  
  // Volumetric calculations
  const calculateVolumetrics = (largo, ancho, alto) => {
    const l = parseFloat(largo) || 0;
    const w = parseFloat(ancho) || 0;
    const h = parseFloat(alto) || 0;
    if (l === 0 || w === 0 || h === 0) return { pvol: 0, volume_ft3: 0 };
    const cubicInches = l * w * h;
    return {
      pvol: (cubicInches / 166).toFixed(2),
      volume_ft3: (cubicInches / 1728).toFixed(2)
    };
  };

  const handleApproveDimensionChange = (field, value) => {
    const newForm = { ...wrFormData, [field]: value };
    const { pvol, volume_ft3 } = calculateVolumetrics(
      field === 'dim_largo' ? value : newForm.dim_largo,
      field === 'dim_ancho' ? value : newForm.dim_ancho,
      field === 'dim_alto' ? value : newForm.dim_alto
    );
    newForm.pvol = pvol;
    newForm.volume_ft3 = volume_ft3;
    const pp = parseFloat(newForm.pp_weight_lb) || 0;
    const pvolNum = parseFloat(pvol) || 0;
    newForm.pmax = Math.max(pp, pvolNum).toFixed(2);
    setWrFormData(newForm);
  };

  const handleApprovePPWeightChange = (value) => {
    const pp = parseFloat(value) || 0;
    const pvolNum = parseFloat(wrFormData.pvol) || 0;
    setWrFormData(prev => ({
      ...prev,
      pp_weight_lb: value,
      pmax: Math.max(pp, pvolNum).toFixed(2)
    }));
  };

  const handleApproveShippingMethodChange = (method) => {
    const { pvol, volume_ft3 } = calculateVolumetrics(
      wrFormData.dim_largo, wrFormData.dim_ancho, wrFormData.dim_alto
    );
    const pp = parseFloat(wrFormData.pp_weight_lb) || 0;
    const pvolNum = parseFloat(pvol) || 0;
    setWrFormData(prev => ({
      ...prev,
      shipping_method: method,
      pvol,
      volume_ft3,
      pmax: Math.max(pp, pvolNum).toFixed(2)
    }));
  };

  const handleOpenApproveModal = (repack) => {
    const repackTrackings = getRepackTrackings(repack);
    const totalWeight = repackTrackings.reduce((sum, t) => sum + (parseFloat(t.weight) || 0), 0);
    setSelectedRepack(repack);
    setWrFormData({
      selectedTrackingIds: [...(repack.tracking_ids || [])],
      wr_number: '',
      shipping_method: repack.shipping_method || 'Aereo',
      package_type: 'Caja',
      pieces: 1,
      dim_largo: '',
      dim_ancho: '',
      dim_alto: '',
      pp_weight_lb: totalWeight.toFixed(2),
      pvol: '',
      pmax: '',
      volume_ft3: '',
      notes: repack.notes || '',
      agent: 'Ninguno',
      description: ''
    });
    setApproveModalOpen(true);
  };

  // Toggle tracking in approval modal
  const toggleApproveTrackingSelection = (trackingId) => {
    setWrFormData(prev => {
      const newIds = prev.selectedTrackingIds.includes(trackingId)
        ? prev.selectedTrackingIds.filter(id => id !== trackingId)
        : [...prev.selectedTrackingIds, trackingId];
      
      // Recalculate weight from selected trackings
      const selectedTracks = trackings.filter(t => newIds.includes(t.id));
      const totalWeight = selectedTracks.reduce((sum, t) => sum + (parseFloat(t.weight) || 0), 0);
      
      const pp = totalWeight;
      const pvolNum = parseFloat(prev.pvol) || 0;
      
      return {
        ...prev,
        selectedTrackingIds: newIds,
        pp_weight_lb: totalWeight.toFixed(2),
        pmax: Math.max(pp, pvolNum).toFixed(2)
      };
    });
  };

  const toggleAllApproveTrackings = () => {
    if (selectedRepack) {
      const allIds = selectedRepack.tracking_ids || [];
      if (wrFormData.selectedTrackingIds.length === allIds.length) {
        setWrFormData(prev => ({ ...prev, selectedTrackingIds: [], pp_weight_lb: '0.00', pmax: '0.00' }));
      } else {
        const allTracks = trackings.filter(t => allIds.includes(t.id));
        const totalWeight = allTracks.reduce((sum, t) => sum + (parseFloat(t.weight) || 0), 0);
        const pvolNum = parseFloat(wrFormData.pvol) || 0;
        setWrFormData(prev => ({
          ...prev,
          selectedTrackingIds: allIds,
          pp_weight_lb: totalWeight.toFixed(2),
          pmax: Math.max(totalWeight, pvolNum).toFixed(2)
        }));
      }
    }
  };

  // Submit approval with complete WR data
  const handleApproveAndCreateWR = async () => {
    if (wrFormData.selectedTrackingIds.length === 0) {
      alert('Debe seleccionar al menos un tracking');
      return;
    }
    if (!wrFormData.dim_largo || !wrFormData.dim_ancho || !wrFormData.dim_alto) {
      alert('Debe ingresar las dimensiones del paquete (Largo x Ancho x Alto)');
      return;
    }
    if (!wrFormData.pp_weight_lb) {
      alert('Debe ingresar el peso (PP) del paquete');
      return;
    }
    
    setApproving(true);
    try {
      const dimensionsIn = `${wrFormData.dim_largo}x${wrFormData.dim_ancho}x${wrFormData.dim_alto}`;
      
      const response = await axios.post(
        `${API}/admin/repacks/${selectedRepack.id}/approve-and-create-wr-custom`,
        {
          selected_tracking_ids: wrFormData.selectedTrackingIds,
          wr_number: wrFormData.wr_number || undefined,
          shipping_method: wrFormData.shipping_method,
          package_type: wrFormData.package_type,
          pieces: parseInt(wrFormData.pieces) || 1,
          final_weight_lb: parseFloat(wrFormData.pp_weight_lb) || 0,
          final_dimensions_in: dimensionsIn,
          volume: parseFloat(wrFormData.volume_ft3) || 0,
          volumetric_weight: parseFloat(wrFormData.pvol) || 0,
          pmax: parseFloat(wrFormData.pmax) || 0,
          notes: wrFormData.notes,
          agent: wrFormData.agent,
          description: wrFormData.description
        },
        axiosConfig
      );
      alert(`${response.data.message}\nWR: ${response.data.wr_number}`);
      setApproveModalOpen(false);
      fetchData();
    } catch (error) {
      alert('Error al aprobar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setApproving(false);
    }
  };

  // ===== FILTERS =====
  const filteredRepacks = repacks.filter(repack => {
    const client = getClientInfo(repack.client_id);
    const matchesSearch = !filters.search || 
      client.full_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      repack.id.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = filters.estatus === 'todos' ||
      (filters.estatus === 'solicitado' && repack.status === 'requested') ||
      (filters.estatus === 'procesando' && repack.status === 'processing') ||
      (filters.estatus === 'completado' && repack.status === 'completed');
    
    const matchesShipping = filters.tipoEnvio === 'cualquiera' ||
      repack.shipping_method?.toLowerCase() === filters.tipoEnvio.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesShipping;
  });

  const getStatusBadge = (status) => {
    switch(status) {
      case 'requested':
        return <span className="bg-yellow-500 text-white px-2 py-1 rounded text-xs font-semibold">SOLICITADO</span>;
      case 'processing':
        return <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-semibold">PROCESANDO</span>;
      case 'completed':
        return <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">COMPLETADO</span>;
      default:
        return <span className="bg-gray-500 text-white px-2 py-1 rounded text-xs font-semibold">{status}</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border-b pb-4">
        <h1 className="text-2xl font-light text-gray-700 mb-4">Solicitudes de Reempaques</h1>
        <nav className="flex text-sm text-gray-500">
          <span className="text-blue-600 hover:underline cursor-pointer">Crear Paquete</span>
          <span className="mx-2">/</span>
          <span className="text-blue-600 hover:underline cursor-pointer">Listar todos</span>
          <span className="mx-2">/</span>
          <span className="text-blue-600 hover:underline cursor-pointer">Gestionar Paquetes</span>
          <span className="mx-2">/</span>
          <span>Solicitudes de Reempaques</span>
        </nav>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-sm text-gray-600">Busqueda</Label>
              <Input
                data-testid="repack-search-input"
                placeholder="Numero,Casillero,Cliente,Destinatario"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Agencia</Label>
              <Select value={filters.agencia} onValueChange={(v) => setFilters({...filters, agencia: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="traetelodafuera">Traetelodafuera</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Tipo de Envio</Label>
              <Select value={filters.tipoEnvio} onValueChange={(v) => setFilters({...filters, tipoEnvio: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cualquiera">Cualquiera</SelectItem>
                  <SelectItem value="maritimo">Maritimo</SelectItem>
                  <SelectItem value="aereo">Aereo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-sm text-gray-600">Desde</Label>
              <Input
                type="date"
                value={filters.desde}
                onChange={(e) => setFilters({...filters, desde: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Hasta</Label>
              <Input
                type="date"
                value={filters.hasta}
                onChange={(e) => setFilters({...filters, hasta: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Tipo</Label>
              <Select value={filters.tipo} onValueChange={(v) => setFilters({...filters, tipo: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="repack">Reempaque</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Estatus</Label>
              <Select value={filters.estatus} onValueChange={(v) => setFilters({...filters, estatus: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="solicitado">Solicitado</SelectItem>
                  <SelectItem value="procesando">Procesando</SelectItem>
                  <SelectItem value="completado">Completado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleSelectAll}>
          {selectedRepacks.length === filteredRepacks.length ? 'Ninguna' : 'Todas'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => {
          if (selectedRepacks.length === 0) {
            alert('Selecciona al menos una solicitud de reempaque');
            return;
          }
          alert(`Tienes ${selectedRepacks.length} solicitud(es) seleccionada(s).`);
        }}>
          Acciones en lote
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700">Accion</th>
                <th className="text-left p-3 font-semibold text-gray-700">Numero</th>
                <th className="text-left p-3 font-semibold text-gray-700">Tipo</th>
                <th className="text-left p-3 font-semibold text-gray-700">Casillero</th>
                <th className="text-left p-3 font-semibold text-gray-700">Cliente</th>
                <th className="text-left p-3 font-semibold text-gray-700">Destinatario</th>
                <th className="text-left p-3 font-semibold text-gray-700">Solicitud</th>
                <th className="text-left p-3 font-semibold text-gray-700">Estatus</th>
                <th className="text-left p-3 font-semibold text-gray-700">F. de Operacion</th>
                <th className="text-left p-3 font-semibold text-gray-700">Tipo de Envio</th>
                <th className="text-left p-3 font-semibold text-gray-700">TrackingsPqt/WR</th>
                <th className="text-left p-3 font-semibold text-gray-700">Instrucciones</th>
                <th className="text-left p-3 font-semibold text-gray-700">Agencia</th>
                <th className="text-left p-3 font-semibold text-gray-700">Tracking</th>
              </tr>
            </thead>
            <tbody>
              {filteredRepacks.map((repack) => {
                const client = getClientInfo(repack.client_id);
                const repackTrackings = getRepackTrackings(repack);
                return (
                  <tr key={repack.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={selectedRepacks.includes(repack.id)}
                          onChange={() => toggleSelectRepack(repack.id)}
                          className="w-4 h-4"
                        />
                        {repack.status === 'requested' && (
                          <button 
                            data-testid={`approve-repack-${repack.id}`}
                            className="bg-green-500 text-white p-1 rounded hover:bg-green-600"
                            onClick={() => handleOpenApproveModal(repack)}
                            title="Aprobar y crear WR"
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        {(repack.status === 'ready_for_wr' || (repack.status === 'processing' && !repack.wr_id)) && (
                          <button 
                            className="bg-orange-500 text-white p-1 rounded hover:bg-orange-600"
                            onClick={() => handleOpenApproveModal(repack)}
                            title="Crear Warehouse Receipt"
                          >
                            <Package size={14} />
                          </button>
                        )}
                        <button 
                          className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600"
                          onClick={() => handleViewRepack(repack)}
                          title="Ver detalles"
                        >
                          <Eye size={14} />
                        </button>
                        <button 
                          className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                          onClick={() => handleDeleteRepack(repack.id)}
                          title="Eliminar solicitud"
                        >
                          <Trash2 size={14} />
                        </button>
                        <button 
                          data-testid={`edit-repack-${repack.id}`}
                          className="bg-blue-400 text-white p-1 rounded hover:bg-blue-500"
                          onClick={() => handleEditRepack(repack)}
                          title="Editar solicitud"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="inline-block bg-green-500 text-white px-3 py-1 rounded text-xs font-semibold">
                        RP{repack.id.substring(0, 4).toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600">Paquete</td>
                    <td className="p-3">
                      <span className="inline-block bg-blue-500 text-white px-3 py-1 rounded text-xs font-semibold">
                        {client.locker_id || 'N/A'}
                      </span>
                    </td>
                    <td className="p-3 text-blue-600 hover:underline cursor-pointer">
                      {client.full_name || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600">
                      {client.full_name || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600 text-xs">
                      {new Date(repack.created_at).toLocaleDateString('es-ES', {
                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </td>
                    <td className="p-3">{getStatusBadge(repack.status)}</td>
                    <td className="p-3 text-gray-600 text-xs">
                      {repack.updated_at 
                        ? new Date(repack.updated_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600">{repack.shipping_method || 'N/A'}</td>
                    <td className="p-3 text-gray-600">
                      {repackTrackings.length > 0 ? (
                        <div className="text-xs">
                          {repackTrackings.map(t => (
                            <div key={t.id} className="mb-1">{t.tracking_number}</div>
                          ))}
                          {(() => {
                            const selected = repackTrackings.length;
                            const available = getClientAvailableCount(repack.client_id);
                            return (
                              <div className={`mt-1 px-2 py-0.5 rounded text-xs font-semibold inline-block ${
                                selected < available ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {selected}/{available} paquetes
                              </div>
                            );
                          })()}
                        </div>
                      ) : 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600 text-xs max-w-xs truncate">{repack.notes || 'N/A'}</td>
                    <td className="p-3 text-gray-600 text-xs">Traetelodafuera_miami</td>
                    <td className="p-3 text-gray-600 text-xs">
                      {repackTrackings.length > 0 
                        ? repackTrackings.map(t => t.tracking_number).join(', ')
                        : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredRepacks.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron solicitudes de reempaque
          </div>
        )}
      </div>

      {/* ===== VIEW MODAL ===== */}
      {viewModalOpen && selectedRepack && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Detalles de Solicitud de Reempaque</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">ID:</Label>
                  <p className="text-gray-700">RP{selectedRepack.id.substring(0, 8).toUpperCase()}</p>
                </div>
                <div>
                  <Label className="font-semibold">Cliente:</Label>
                  <p className="text-gray-700">{getClientInfo(selectedRepack.client_id).full_name}</p>
                </div>
                <div>
                  <Label className="font-semibold">Metodo de Envio:</Label>
                  <p className="text-gray-700">{selectedRepack.shipping_method}</p>
                </div>
                <div>
                  <Label className="font-semibold">Estado:</Label>
                  <p className="text-gray-700">{selectedRepack.status}</p>
                </div>
                <div className="col-span-2">
                  <Label className="font-semibold">Notas:</Label>
                  <p className="text-gray-700">{selectedRepack.notes || 'Sin notas'}</p>
                </div>
              </div>
              <div>
                <Label className="font-semibold mb-2 block">Paquetes incluidos ({selectedRepack.tracking_ids.length}):</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">Tracking</th>
                        <th className="p-2 text-left">Descripcion</th>
                        <th className="p-2 text-left">Peso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getRepackTrackings(selectedRepack).map(tracking => (
                        <tr key={tracking.id} className="border-t">
                          <td className="p-2">{tracking.tracking_number}</td>
                          <td className="p-2">{tracking.description || 'N/A'}</td>
                          <td className="p-2">{tracking.weight || 'N/A'} lb</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setViewModalOpen(false)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EDIT MODAL (with tracking multi-select) ===== */}
      {editModalOpen && selectedRepack && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Editar Solicitud de Reempaque</h2>
              <button onClick={() => setEditModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Basic fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Metodo de Envio</Label>
                  <Select 
                    value={editFormData.shipping_method} 
                    onValueChange={(value) => setEditFormData({...editFormData, shipping_method: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aereo">Aereo</SelectItem>
                      <SelectItem value="Maritimo">Maritimo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notas/Instrucciones</Label>
                  <Input 
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    placeholder="Instrucciones especiales..."
                  />
                </div>
              </div>

              {/* Tracking multi-select */}
              <div>
                <Label className="font-semibold text-lg mb-2 block">Trackings Seleccionados</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Puedes agregar o quitar trackings de esta solicitud. Los trackings disponibles del cliente se muestran abajo.
                </p>
                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left w-10"></th>
                        <th className="p-2 text-left">Tracking</th>
                        <th className="p-2 text-left">Descripcion</th>
                        <th className="p-2 text-left">Peso (lb)</th>
                        <th className="p-2 text-left">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getClientTrackingsForEdit(selectedRepack.client_id, selectedRepack.tracking_ids).map(tracking => {
                        const isSelected = editFormData.tracking_ids.includes(tracking.id);
                        const wasOriginal = selectedRepack.tracking_ids.includes(tracking.id);
                        return (
                          <tr 
                            key={tracking.id} 
                            className={`border-t cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-green-50' : ''}`}
                            onClick={() => toggleEditTrackingSelection(tracking.id)}
                          >
                            <td className="p-2">
                              <input 
                                type="checkbox" 
                                checked={isSelected}
                                onChange={() => toggleEditTrackingSelection(tracking.id)}
                                className="w-4 h-4"
                              />
                            </td>
                            <td className="p-2 font-mono text-xs">{tracking.tracking_number}</td>
                            <td className="p-2">{tracking.description || 'Sin descripcion'}</td>
                            <td className="p-2">{tracking.weight || '0'}</td>
                            <td className="p-2">
                              {wasOriginal && isSelected && (
                                <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">En solicitud</span>
                              )}
                              {!wasOriginal && isSelected && (
                                <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">Nuevo</span>
                              )}
                              {wasOriginal && !isSelected && (
                                <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs">Removido</span>
                              )}
                              {!wasOriginal && !isSelected && (
                                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">Disponible</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {editFormData.tracking_ids.length} tracking(s) seleccionado(s)
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancelar</Button>
              <Button 
                data-testid="save-edit-repack-btn"
                onClick={handleSaveEdit} 
                disabled={savingEdit || editFormData.tracking_ids.length === 0}
              >
                {savingEdit ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== APPROVE & CREATE WR MODAL (Complete form like Crear WR desde Trackings) ===== */}
      {approveModalOpen && selectedRepack && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package size={24} className="text-green-600" />
                Aprobar Reempaque y Crear Warehouse Receipt
              </h2>
              <button onClick={() => setApproveModalOpen(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                <X size={24} />
              </button>
            </div>
            
            {/* Client Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-800 mb-2">Informacion del Cliente</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Cliente:</span>
                  <span className="ml-2 font-medium">{getClientInfo(selectedRepack.client_id).full_name}</span>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2">{getClientInfo(selectedRepack.client_id).email}</span>
                </div>
                <div>
                  <span className="text-gray-600">Casillero:</span>
                  <span className="ml-2 font-medium">{getClientInfo(selectedRepack.client_id).locker_id || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Step 1: Select Trackings */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-lg text-blue-700">1. Paquetes a Incluir en el WR</h3>
                <Button variant="outline" size="sm" onClick={toggleAllApproveTrackings}>
                  {wrFormData.selectedTrackingIds.length === (selectedRepack.tracking_ids?.length || 0) 
                    ? 'Deseleccionar Todos' 
                    : 'Seleccionar Todos'}
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left w-10"></th>
                      <th className="p-2 text-left">Tracking</th>
                      <th className="p-2 text-left">Descripcion</th>
                      <th className="p-2 text-left">Peso (lb)</th>
                      <th className="p-2 text-left">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getRepackTrackings(selectedRepack).map(tracking => (
                      <tr 
                        key={tracking.id} 
                        className={`border-t cursor-pointer hover:bg-gray-50 ${
                          wrFormData.selectedTrackingIds.includes(tracking.id) ? 'bg-green-50' : ''
                        }`}
                        onClick={() => toggleApproveTrackingSelection(tracking.id)}
                      >
                        <td className="p-2">
                          <input 
                            type="checkbox" 
                            checked={wrFormData.selectedTrackingIds.includes(tracking.id)}
                            onChange={() => toggleApproveTrackingSelection(tracking.id)}
                            className="w-4 h-4"
                          />
                        </td>
                        <td className="p-2 font-mono text-xs">{tracking.tracking_number}</td>
                        <td className="p-2">{tracking.description || 'Sin descripcion'}</td>
                        <td className="p-2">{tracking.weight || '0'}</td>
                        <td className="p-2">${tracking.declared_value || '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {wrFormData.selectedTrackingIds.length} de {selectedRepack.tracking_ids?.length || 0} paquetes seleccionados
              </p>
            </div>

            {/* Step 2: WR Data (same layout as Crear WR desde Trackings) */}
            {wrFormData.selectedTrackingIds.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-4 text-blue-700">2. Datos del Paquete Final (Consolidado)</h3>
                
                {/* WR Number (optional) */}
                <div className="bg-green-50 p-3 rounded border border-green-200 mb-4">
                  <Label className="text-sm font-semibold text-green-800">Numero de WR (Opcional)</Label>
                  <Input
                    data-testid="approve-wr-number-input"
                    value={wrFormData.wr_number}
                    onChange={(e) => setWrFormData({...wrFormData, wr_number: e.target.value})}
                    placeholder="Dejar vacio para auto-generar (Ej: WR20XXX)"
                    className="mt-1"
                  />
                  <p className="text-xs text-green-600 mt-1">
                    Asigna el numero de WR de tu proveedor de cargas. Si lo dejas vacio se genera automaticamente.
                  </p>
                </div>

                {/* Method, Type, Pieces, Agent */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <Label>Metodo de Envio *</Label>
                    <Select value={wrFormData.shipping_method} onValueChange={handleApproveShippingMethodChange}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Aereo">Aereo</SelectItem>
                        <SelectItem value="Maritimo">Maritimo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select value={wrFormData.package_type} onValueChange={(v) => setWrFormData({...wrFormData, package_type: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Caja">Caja</SelectItem>
                        <SelectItem value="Sobre">Sobre</SelectItem>
                        <SelectItem value="Paquete">Paquete</SelectItem>
                        <SelectItem value="Bulto">Bulto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cantidad</Label>
                    <Input 
                      type="number" min="1"
                      value={wrFormData.pieces}
                      onChange={(e) => setWrFormData({...wrFormData, pieces: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Agente</Label>
                    <Input 
                      value={wrFormData.agent}
                      onChange={(e) => setWrFormData({...wrFormData, agent: e.target.value})}
                      placeholder="Ninguno"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Dimensions */}
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold mb-3 text-blue-800">Dimensiones (pulgadas / inches)</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Largo (L) *</Label>
                      <Input 
                        type="number" step="0.1"
                        value={wrFormData.dim_largo}
                        onChange={(e) => handleApproveDimensionChange('dim_largo', e.target.value)}
                        placeholder="Ej: 18"
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div>
                      <Label>Ancho (W) *</Label>
                      <Input 
                        type="number" step="0.1"
                        value={wrFormData.dim_ancho}
                        onChange={(e) => handleApproveDimensionChange('dim_ancho', e.target.value)}
                        placeholder="Ej: 14"
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div>
                      <Label>Alto (H) *</Label>
                      <Input 
                        type="number" step="0.1"
                        value={wrFormData.dim_alto}
                        onChange={(e) => handleApproveDimensionChange('dim_alto', e.target.value)}
                        placeholder="Ej: 12"
                        className="mt-1 bg-white"
                      />
                    </div>
                  </div>
                  {wrFormData.dim_largo && wrFormData.dim_ancho && wrFormData.dim_alto && (
                    <p className="text-sm text-blue-600 mt-2">
                      Dimensiones: {wrFormData.dim_largo}" x {wrFormData.dim_ancho}" x {wrFormData.dim_alto}"
                    </p>
                  )}
                </div>

                {/* Weights and Calculations */}
                <div className={`p-4 rounded-lg mb-4 ${wrFormData.shipping_method === 'Aereo' ? 'bg-sky-50' : 'bg-green-50'}`}>
                  <h4 className="font-semibold mb-3">
                    <span className={wrFormData.shipping_method === 'Aereo' ? 'text-sky-800' : 'text-green-800'}>
                      {wrFormData.shipping_method === 'Aereo' ? 'Calculos Aereo' : 'Calculos Maritimo'}
                    </span>
                  </h4>
                  
                  {wrFormData.shipping_method === 'Aereo' ? (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>PP - Peso Pesado (lb) *</Label>
                        <Input 
                          type="number" step="0.01"
                          value={wrFormData.pp_weight_lb}
                          onChange={(e) => handleApprovePPWeightChange(e.target.value)}
                          placeholder="Peso real en libras"
                          className="mt-1 bg-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">Peso real de la caja</p>
                      </div>
                      <div>
                        <Label>PVol - Peso Volumetrico</Label>
                        <Input 
                          type="number" step="0.01"
                          value={wrFormData.pvol}
                          readOnly
                          className="mt-1 bg-gray-100 font-semibold"
                        />
                        <p className="text-xs text-gray-500 mt-1">(LxWxH) / 166</p>
                      </div>
                      <div>
                        <Label>PMax (Mayor)</Label>
                        <Input 
                          type="number" step="0.01"
                          value={wrFormData.pmax}
                          readOnly
                          className="mt-1 bg-yellow-100 font-bold text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">El mayor entre PP y PVol</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>PP - Peso Pesado (lb) *</Label>
                        <Input 
                          type="number" step="0.01"
                          value={wrFormData.pp_weight_lb}
                          onChange={(e) => handleApprovePPWeightChange(e.target.value)}
                          placeholder="Peso real en libras"
                          className="mt-1 bg-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">Peso real de la caja</p>
                      </div>
                      <div>
                        <Label>Volumen (ft3)</Label>
                        <Input 
                          type="number" step="0.01"
                          value={wrFormData.volume_ft3}
                          readOnly
                          className="mt-1 bg-green-100 font-bold text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">(LxWxH) / 1728</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description and Notes */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Descripcion</Label>
                    <Input 
                      value={wrFormData.description}
                      onChange={(e) => setWrFormData({...wrFormData, description: e.target.value})}
                      placeholder="Descripcion del contenido"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Notas / Instrucciones</Label>
                    <Input 
                      value={wrFormData.notes}
                      onChange={(e) => setWrFormData({...wrFormData, notes: e.target.value})}
                      placeholder="Instrucciones especiales..."
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Summary Box */}
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <h4 className="font-semibold mb-2">Resumen del Paquete</h4>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Tipo:</span>
                      <span className="ml-1 font-medium">{wrFormData.package_type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Envio:</span>
                      <span className="ml-1 font-medium">{wrFormData.shipping_method}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Trackings:</span>
                      <span className="ml-1 font-medium">{wrFormData.selectedTrackingIds.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{wrFormData.shipping_method === 'Aereo' ? 'PMax:' : 'Vol ft3:'}</span>
                      <span className="ml-1 font-bold text-blue-600">
                        {wrFormData.shipping_method === 'Aereo' ? wrFormData.pmax || '0' : wrFormData.volume_ft3 || '0'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={() => setApproveModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                data-testid="approve-create-wr-btn"
                onClick={handleApproveAndCreateWR}
                disabled={approving || wrFormData.selectedTrackingIds.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {approving ? 'Creando WR...' : `Aprobar y Crear WR (${wrFormData.selectedTrackingIds.length} trackings)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LockerRepacks;
